import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  ACCEPT_PRIVACY_POLICY,
  ACCEPT_TERM_AND_SERVICE,
  fetchSessionData,
  GraphQLClient,
} from '@rapid-recovery-agency-inc/insightt-shared';
import { Settings } from 'luxon';

import { InsighttNotification } from '../../../helpers/insightt-notification';
import { client } from '../../../shared/apollo';
// T031 — `import type` (rather than a value import of a type-only name) so this specifier is
// guaranteed to be elided at compile time by every toolchain (tsc, esbuild, swc), regardless of
// per-file usage-analysis heuristics. Confirmed necessary, not cosmetic: with a plain value
// import, a test that imports this module directly (bypassing the app's normal `src/redux/store`
// boot order) can trip a real circular-import hazard — `../../../redux/store` -> `./reducers`
// (barrel) -> `./system/redux/reducers` -> this module -> `../../../redux/store` again, mid-init —
// which left `fetchSession` transiently undefined while `reducers.ts`'s `createReducer` builder
// ran. That hazard pre-dates T031 (this import already existed); this `import type` removes it.
import type { AppThunkConfig } from '../../../redux/store';
// T037 (FR-008, FR-021) — mode-neutral (via `getAppAuthBoundary()`) rather than the raw legacy
// `access_token` cookie reader `getAuthToken()` this used before: an SSO session with no legacy
// cookie present must also trigger this cleanup when the server reports no session, not only a
// legacy one. See `helpers/auth-header.jsx`'s own doc comment, which named this call site as a
// "not-yet-migrated" `getAuthToken()` consumer.
import { getBearerAccessToken } from '../../../helpers/auth-header';
import { cleanSessionData } from '../utils';
import { settleCallbackSession } from '../../auth';
import {
  FETCH_SESSION,
  FetchSessionResponse,
  ON_ACCEPT_PRIVACY_POLICY,
  ON_ACCEPT_TERM_AND_SERVICE,
  RESET_SYSTEM_SCOPE,
} from './types';

let timer: ReturnType<typeof setTimeout> | null = null;

const showNotificationOnce = (type: Parameters<typeof InsighttNotification>[0], message: string) => {
  if (timer) return;
  InsighttNotification(type, message);
  timer = setTimeout(() => {
    timer = null;
  }, 3000); // Notification can be shown again after 3 seconds
};

/**
 * Clears the identity-scoped session slice (user/company/branches/roles/subscription/etc.)
 * immediately, without waiting for the next {@link fetchSession} round trip.
 *
 * `AuthRoute` (T031) dispatches this the moment the auth boundary transitions from authenticated
 * to unauthenticated (logout, unauthorized cleanup, or an interaction-required renewal
 * settlement) — see spec.md's "a second user observes no prior user/company/branch/cache/identity
 * state" edge case and the constitution's Principle I ("[r]e-validate branch and company scope
 * when the selection changes, and refetch or invalidate any cached data that was scoped to the
 * previous selection"). Without this, the previous identity's company/branch scope would remain
 * readable from this Redux slice for the entire gap between the boundary clearing its own state
 * and the next `fetchSession` resolving — e.g. across a full sign-out/sign-in cycle on a shared
 * browser.
 */
export const resetSystemScope = createAction(RESET_SYSTEM_SCOPE);

export const fetchSession = createAsyncThunk<FetchSessionResponse, void, AppThunkConfig>(
  FETCH_SESSION,
  async (_, { fulfillWithValue, rejectWithValue, getState, requestId }) => {
    // Only the latest check may settle a waiting SSO callback: an older, overlapping check can answer
    // anonymously from before the Backend session existed.
    const isLatestRequest = () => getState().system?.latestFetchSessionRequestId === requestId;
    try {
      const session = await fetchSessionData(client as GraphQLClient);

      Settings.defaultLocale = 'en';

      if (session?.timezone) {
        Settings.defaultZone = session?.timezone;
      }

      // Settles a valid SSO callback that is waiting for its Backend session (T105).
      if (isLatestRequest()) {
        settleCallbackSession(session?.user ? 'established' : 'unauthorized');
      }

      if (!session?.user && getBearerAccessToken()) {
        /*
        This cleaning is necessary in cases where sessions are deleted
        without logging out, e.g. when a subscription is downgraded
        from basic to advanced, or when a downgrade is made, etc.
      */
        await cleanSessionData();
      }

      return fulfillWithValue({ session });
    } catch {
      if (isLatestRequest()) {
        settleCallbackSession('unavailable');
      }
      showNotificationOnce('error', 'Error fetching session');
      return rejectWithValue({});
    }
  },
);

export const acceptTermAndService = createAsyncThunk<
  { success: boolean; termAndConditionId: number },
  { termAndConditionId: number }
>(ON_ACCEPT_TERM_AND_SERVICE, async ({ termAndConditionId }, { fulfillWithValue, rejectWithValue }) => {
  try {
    const response = await client.query({
      query: ACCEPT_TERM_AND_SERVICE,
      variables: { termAndConditionId },
    });
    return fulfillWithValue({
      success: response?.data?.success,
      termAndConditionId,
    });
  } catch (e) {
    InsighttNotification('error', 'Something went wrong', e);
    return rejectWithValue(e);
  }
});

export const acceptPrivacyPolicy = createAsyncThunk<
  { success: boolean; privacyPolicyId: number },
  { privacyPolicyId: number }
>(ON_ACCEPT_PRIVACY_POLICY, async ({ privacyPolicyId }, { fulfillWithValue, rejectWithValue }) => {
  try {
    const response = await client.query({
      query: ACCEPT_PRIVACY_POLICY,
      variables: { privacyPolicyId },
    });
    return fulfillWithValue({
      success: response?.data?.success,
      privacyPolicyId,
    });
  } catch (e) {
    InsighttNotification('error', 'Something went wrong', e);
    return rejectWithValue(e);
  }
});
